import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Menu, X } from 'lucide-react';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pendingScroll, setPendingScroll] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // After navigating to '/', scroll to services section
  useEffect(() => {
    if (pendingScroll && location.pathname === '/') {
      const el = document.getElementById('servicios');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setPendingScroll(false);
      } else {
        // Element not yet in DOM — retry briefly
        const timer = setTimeout(() => {
          document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' });
          setPendingScroll(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [pendingScroll, location.pathname]);

  const handleServicesClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      setPendingScroll(true);
      navigate('/');
    } else {
      document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const navLinks = [
    { key: 'nav.home', to: '/', isPage: true },
    { key: 'nav.services', to: '/#servicios', isPage: false, onClick: handleServicesClick },
    { key: 'nav.portfolio', to: '/portafolio', isPage: true },
    { key: 'nav.courses', to: '/cursos', isPage: true },
    { key: 'nav.podcast', to: '/podcast', isPage: true },
    { key: 'nav.blog', to: '/blog', isPage: true },
    { key: 'nav.about', to: '/acerca', isPage: true },
    { key: 'nav.contact', to: '/contacto', isPage: true },
  ];

  const toggleLang = () => i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');

  const isActive = (to) => location.pathname === to;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-[background-color,box-shadow,border-color,backdrop-filter,padding,opacity] duration-300 ${
        scrolled
          ? 'backdrop-blur-md bg-white/90 dark:bg-[#0F1C2E]/90 shadow-sm border-b border-black/5 dark:border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center group">
            <img
              src="/logo-light.svg"
              alt="Mikel Media"
              className="block dark:hidden h-12 w-auto transition-opacity group-hover:opacity-80"
            />
            <img
              src="/logo-dark-coral.svg"
              alt="Mikel Media"
              className="hidden dark:block h-12 w-auto transition-opacity group-hover:opacity-80"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            {navLinks.map(link => (
              link.isPage ? (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.key.replace('nav.', '')}`}
                  className={`text-sm font-medium font-poppins transition-colors hover:text-[#7D468C] ${
                    isActive(link.to) ? 'text-[#7D468C]' : 'text-[#233559] dark:text-white/80'
                  }`}
                >
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.to}
                  href={link.to}
                  onClick={link.onClick}
                  data-testid={`nav-${link.key.replace('nav.', '')}`}
                  className="text-sm font-medium font-poppins text-[#233559] dark:text-white/80 transition-colors hover:text-[#7D468C] cursor-pointer"
                >
                  {t(link.key)}
                </a>
              )
            ))}
          </div>

          {/* Right controls */}
          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className="text-xs font-bold font-poppins text-[#233559] dark:text-white/80 hover:text-[#7D468C] dark:hover:text-[#7D468C] transition-colors px-3 py-1 border border-[#233559]/20 dark:border-white/20 hover:border-[#7D468C]"
            >
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 text-[#233559] dark:text-white/80 hover:text-[#7D468C] dark:hover:text-[#7D468C] transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>

          {/* Mobile menu btn */}
          <button
            className="md:hidden p-2 text-[#233559] dark:text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white dark:bg-[#0F1C2E] border-t border-gray-100 dark:border-gray-800 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map(link => (
              link.isPage ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block px-3 py-2.5 text-sm font-medium font-poppins text-[#233559] dark:text-white hover:text-[#7D468C] hover:bg-[#7D468C]/5 transition-colors"
                >
                  {t(link.key)}
                </Link>
              ) : (
                <a
                  key={link.to}
                  href={link.to}
                  onClick={link.onClick}
                  className="block px-3 py-2.5 text-sm font-medium font-poppins text-[#233559] dark:text-white hover:text-[#7D468C] hover:bg-[#7D468C]/5 transition-colors cursor-pointer"
                >
                  {t(link.key)}
                </a>
              )
            ))}
            <div className="flex items-center gap-3 px-3 pt-3 mt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={toggleLang}
                className="text-xs font-bold font-poppins text-[#233559] dark:text-white border border-[#233559]/20 dark:border-white/20 px-3 py-1"
              >
                {i18n.language === 'es' ? 'EN' : 'ES'}
              </button>
              <button onClick={toggleTheme} className="p-1 text-[#233559] dark:text-white">
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
